import React, { useEffect } from 'react';
import { useMutation } from 'react-query';

import { Form } from 'antd';
import styled from 'styled-components';

import { createCommunityContact, updateCommunityContact } from 'apis/CommunityAPI';
import { useRootContext } from 'components/layout/RootContext';
import Button from 'components/lib/Button';
import Checkbox from 'components/lib/Checkbox';
import Input from 'components/lib/Input';
import PhoneInput, { PHONE_INPUT_VALIDATION_RULES } from 'components/lib/PhoneInput';
import Radio from 'components/lib/Radio';
import { SimpleListEntryModal } from 'components/lib/SimpleListModal';
import {
  SimpleListEntryFormContainer,
  SimpleListFieldGroup,
  SimpleListFieldInputContainer,
  SimpleListFieldLabel,
  SimpleListModalFooter,
} from 'components/lib/SimpleListModalComponents';
import {
  COMMUNITY_CONTACT_DELIVERY_METHOD_LABELS,
  COMMUNITY_CONTACT_TYPE_LABELS,
  CommunityContactDeliveryMethodEnum,
  CommunityContactSourceEnum,
  CommunityContactTypeEnum,
} from 'constants/community';
import { getErrorMessageByPriority, getFirstErrorMessage, normalizeEmail, validateEmail } from 'helpers/emails';
import { normalizeNorthAmericanPhone } from 'helpers/phoneNumbers';
import { CommunityContactPayloadType, CommunityContactType } from 'types/communities';

const FieldLabel = styled(SimpleListFieldLabel)`
  width: 60px;
  flex: 0 0 60px;
  text-transform: none;
  letter-spacing: 0;
  font-size: 13px;
  line-height: 1.2;
`;

const ContactEntryModal = styled(SimpleListEntryModal)`
  &&& {
    .ant-modal-header {
      border-bottom: 1px solid var(--line-gray);
      margin-bottom: 24px;
    }

    .ant-modal-body {
      padding-top: 0;
    }
  }
`;

const TextFieldLabel = styled(FieldLabel)`
  min-height: 37px;
  display: flex;
  align-items: center;
`;

const TextInputFieldGroup = styled(SimpleListFieldGroup)`
  align-items: flex-start;
`;

const TextFieldFormItem = styled(Form.Item)`
  .ant-form-item-explain {
    min-height: 20px;
    margin-top: 2px;
  }
`;

const FullWidthFieldInputContainer = styled(SimpleListFieldInputContainer)`
  width: 100%;
  box-sizing: border-box;
`;

const StyledRadioGroup = styled(Radio.Group)`
  display: flex;
  gap: 18px;
  flex-wrap: wrap;
  min-height: 40px;
  align-items: center;

  .ant-radio-wrapper {
    margin-right: 0;
  }
`;

const TypeRadioGroup = styled(StyledRadioGroup)`
  flex-wrap: nowrap;
  gap: 14px;

  .ant-radio-wrapper {
    white-space: nowrap;
  }
`;

const RequiredAsterisk = styled.span`
  color: var(--error-red);
  margin-left: 4px;
`;

const CheckboxLabelSpacer = styled(FieldLabel)`
  color: transparent;
`;

const InlineControlFormItem = styled(Form.Item)`
  display: flex;
  align-items: center;
  min-height: 40px;
`;

type Props = {
  communityContacts: CommunityContactType[];
  communityId?: number;
  item?: CommunityContactType;
  onClose: () => void;
  onSaved: () => void;
  open: boolean;
};

const EMPTY_ENTRY_VALUES: CommunityContactPayloadType = {
  email: '',
  name: '',
  phone: null,
  type: CommunityContactTypeEnum.COMMUNITY_REPRESENTATIVE,
  delivery_method: CommunityContactDeliveryMethodEnum.DIRECT_EMAIL,
  source: CommunityContactSourceEnum.MANUALLY_ADDED,
  shop_request_contact_enabled: true,
};

const ManageContactsEntryModal = ({ communityContacts, communityId, item, onClose, onSaved, open }: Props) => {
  const { showMessage } = useRootContext();
  const [entryForm] = Form.useForm<CommunityContactPayloadType>();
  const entryEmailValue = Form.useWatch('email', entryForm);
  const entryPhoneValue = Form.useWatch('phone', entryForm);

  useEffect(() => {
    if (!open) {
      return;
    }

    entryForm.setFieldsValue(item ?? EMPTY_ENTRY_VALUES);
  }, [entryForm, item, open]);

  const closeEntryModal = React.useCallback(() => {
    entryForm.resetFields();
    onClose();
  }, [entryForm, onClose]);

  const { mutateAsync: createContact, isLoading: isCreating } = useMutation({
    mutationFn: (payload: CommunityContactPayloadType) => createCommunityContact(communityId as number, payload),
    onSuccess: () => {
      onSaved();
      showMessage('success', 'Community contact created successfully.');
      closeEntryModal();
    },
  });

  const { mutateAsync: updateContact, isLoading: isUpdating } = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: CommunityContactPayloadType }) =>
      updateCommunityContact(communityId as number, id, payload),
    onSuccess: () => {
      onSaved();
      showMessage('success', 'Community contact updated successfully.');
      closeEntryModal();
    },
  });

  const handleEntrySubmit = React.useCallback(
    async (values: CommunityContactPayloadType) => {
      const payload: CommunityContactPayloadType = {
        email: normalizeEmail(values.email) || null,
        name: values.name.trim(),
        phone: normalizeNorthAmericanPhone(values.phone) || null,
        type: values.type,
        delivery_method: values.delivery_method,
        source: values.source ?? (item?.id ? item.source : CommunityContactSourceEnum.MANUALLY_ADDED),
        shop_request_contact_enabled: Boolean(values.shop_request_contact_enabled),
      };

      try {
        if (item?.id) {
          await updateContact({ id: item.id, payload });
          return;
        }

        await createContact(payload);
      } catch (error: any) {
        const errorResponse = error?.response?.data;
        const emailErrorMessage = getFirstErrorMessage(errorResponse?.email);
        const phoneErrorMessage = getFirstErrorMessage(errorResponse?.phone);

        if (emailErrorMessage) {
          entryForm.setFields([{ name: 'email', errors: [emailErrorMessage] }]);
        }
        if (phoneErrorMessage) {
          entryForm.setFields([{ name: 'phone', errors: [phoneErrorMessage] }]);
        }

        showMessage(
          'error',
          getErrorMessageByPriority(
            errorResponse,
            item?.id ? 'Failed to update community contact.' : 'Failed to create community contact.',
            ['email', 'name', 'shop_request_contact_enabled', 'non_field_errors', 'detail']
          )
        );
      }
    },
    [createContact, entryForm, item?.id, showMessage, updateContact]
  );

  const isEntrySubmitting = isCreating || isUpdating;
  const normalizedEntryEmail = normalizeEmail(entryEmailValue);
  const normalizedEntryPhone = normalizeNorthAmericanPhone(entryPhoneValue);
  const isEntryContactMethodValid = Boolean(
    (normalizedEntryEmail || normalizedEntryPhone) && (!normalizedEntryEmail || validateEmail(normalizedEntryEmail))
  );

  return (
    <ContactEntryModal
      open={open}
      onCancel={closeEntryModal}
      footer={null}
      title={item?.id ? 'Edit Contact' : 'Add Contact'}
      destroyOnClose
    >
      <SimpleListEntryFormContainer>
        <Form<CommunityContactPayloadType>
          form={entryForm}
          layout="vertical"
          onFinish={(values) => {
            handleEntrySubmit(values);
          }}
          initialValues={EMPTY_ENTRY_VALUES}
        >
          <TextInputFieldGroup>
            <TextFieldLabel>
              Name
              <RequiredAsterisk>*</RequiredAsterisk>
            </TextFieldLabel>
            <SimpleListFieldInputContainer>
              <TextFieldFormItem name="name" rules={[{ required: true, message: 'Please enter a contact name' }]}>
                <Input placeholder="Enter contact name" />
              </TextFieldFormItem>
            </SimpleListFieldInputContainer>
          </TextInputFieldGroup>

          <TextInputFieldGroup>
            <TextFieldLabel>Email</TextFieldLabel>
            <SimpleListFieldInputContainer>
              <TextFieldFormItem
                name="email"
                dependencies={['phone']}
                rules={[
                  {
                    validator: (_, value) => {
                      const normalizedEmail = normalizeEmail(value);
                      const normalizedPhone = normalizeNorthAmericanPhone(entryForm.getFieldValue('phone'));

                      if (!normalizedEmail && !normalizedPhone) {
                        return Promise.reject(new Error('Enter an email address or phone number'));
                      }

                      if (!normalizedEmail) {
                        return Promise.resolve();
                      }

                      if (!validateEmail(normalizedEmail)) {
                        return Promise.reject(new Error('Please enter a valid email address'));
                      }

                      const hasMatchingContact = communityContacts.some(
                        (communityContact) =>
                          normalizeEmail(communityContact.email) === normalizedEmail && communityContact.id !== item?.id
                      );

                      if (hasMatchingContact) {
                        return Promise.reject(
                          new Error('This email address is already registered for this community.')
                        );
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input placeholder="Enter email address" />
              </TextFieldFormItem>
            </SimpleListFieldInputContainer>
          </TextInputFieldGroup>

          <TextInputFieldGroup>
            <TextFieldLabel>Phone</TextFieldLabel>
            <SimpleListFieldInputContainer>
              <TextFieldFormItem name="phone" dependencies={['email']} rules={PHONE_INPUT_VALIDATION_RULES}>
                <PhoneInput />
              </TextFieldFormItem>
            </SimpleListFieldInputContainer>
          </TextInputFieldGroup>

          <SimpleListFieldGroup>
            <FieldLabel>Type</FieldLabel>
            <FullWidthFieldInputContainer>
              <InlineControlFormItem name="type">
                <TypeRadioGroup>
                  <Radio value={CommunityContactTypeEnum.COMMUNITY_REPRESENTATIVE}>
                    {COMMUNITY_CONTACT_TYPE_LABELS[CommunityContactTypeEnum.COMMUNITY_REPRESENTATIVE]}
                  </Radio>
                  <Radio value={CommunityContactTypeEnum.MARKETING_AUTOMATION}>
                    {COMMUNITY_CONTACT_TYPE_LABELS[CommunityContactTypeEnum.MARKETING_AUTOMATION]}
                  </Radio>
                </TypeRadioGroup>
              </InlineControlFormItem>
            </FullWidthFieldInputContainer>
          </SimpleListFieldGroup>

          <SimpleListFieldGroup>
            <FieldLabel>Delivery method</FieldLabel>
            <FullWidthFieldInputContainer>
              <InlineControlFormItem name="delivery_method">
                <StyledRadioGroup>
                  <Radio value={CommunityContactDeliveryMethodEnum.DIRECT_EMAIL}>
                    {COMMUNITY_CONTACT_DELIVERY_METHOD_LABELS[CommunityContactDeliveryMethodEnum.DIRECT_EMAIL]}
                  </Radio>
                  <Radio value={CommunityContactDeliveryMethodEnum.MARKETING_PLATFORM}>
                    {COMMUNITY_CONTACT_DELIVERY_METHOD_LABELS[CommunityContactDeliveryMethodEnum.MARKETING_PLATFORM]}
                  </Radio>
                </StyledRadioGroup>
              </InlineControlFormItem>
            </FullWidthFieldInputContainer>
          </SimpleListFieldGroup>

          <SimpleListFieldGroup>
            <CheckboxLabelSpacer>&nbsp;</CheckboxLabelSpacer>
            <SimpleListFieldInputContainer>
              <Form.Item name="shop_request_contact_enabled" valuePropName="checked">
                <Checkbox className="green">Enable as shop request contact</Checkbox>
              </Form.Item>
            </SimpleListFieldInputContainer>
          </SimpleListFieldGroup>

          <SimpleListModalFooter>
            <Button onClick={closeEntryModal}>Close</Button>
            <Button
              type="primary"
              onClick={() => entryForm.submit()}
              disabled={isEntrySubmitting || !isEntryContactMethodValid}
            >
              Save
            </Button>
          </SimpleListModalFooter>
        </Form>
      </SimpleListEntryFormContainer>
    </ContactEntryModal>
  );
};

export default ManageContactsEntryModal;
